import { useState, useEffect, useCallback } from 'react';
import { menusApi, MenuItemDto } from '@/features/superadmin/roles/api/roles.api';
import { useAuth } from './AuthContext';


export interface DynamicMenuItem {
  title: string;
  path: string;
  iconName: string;
  category: string;
  sortOrder: number;
}

export const useDynamicMenu = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<DynamicMenuItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMenu = useCallback(async () => {
    if (!user) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await menusApi.getMyMenu();
      if (res.data?.data && Array.isArray(res.data.data)) {
        const mapped = res.data.data.map((item: MenuItemDto) => ({
          title: item.title,
          path: item.path,
          iconName: item.icon || 'Menu',
          category: item.category || 'MAIN MENU',
          sortOrder: item.sort_order || 1,
        }));
        setItems(mapped);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load user menus');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchMenu();
    const handleUpdate = () => {
      fetchMenu();
    };
    window.addEventListener('menu-updated', handleUpdate);
    window.addEventListener('roles-updated', handleUpdate);
    return () => {
      window.removeEventListener('menu-updated', handleUpdate);
      window.removeEventListener('roles-updated', handleUpdate);
    };
  }, [fetchMenu, user?.role, JSON.stringify(user?.permissions)]);

  return { items, loading, error, refetch: fetchMenu };
};
